import { IQuery } from '@patsnap/synapse_common_interface'
import { AggBarChartEntity, AggLineChartEntity, AggOneDimDataItem } from '@patsnap/synapse_domain'
import { IQueryService } from '@pharmsnap/shared/composition/useQueryService'
import { ILoadingService, ISearchResult } from '@pharmsnap/shared/src/types'
import { computed, defineComponent, getCurrentInstance, PropType, reactive, ref, watch } from '@vue/composition-api'
import { isEqual } from 'lodash'
import {
  BAnalysisBarEchart,
  BAnalysisBarTable,
  BAnalysisChartCard,
  BAnalysisChartFullScreenCard,
  BAnalysisLineEchart,
  BAnalysisLineTable,
  useAnalysisLiteratureOneDimChartEntity,
} from '../../BAnalysisChartNext'
import { useAnalysisOneDimChartNavList } from '../../BAnalysisChartNext/compositions/useAnalysisOneDimChartNavList'
import $classes from '../BAnalysisView.module.scss'
import cn from '../locales/cn.json'
import en from '../locales/en.json'
import { barChartTypes, IUseLiteratureAggChartType, lineChartTypes, useLiteratureAggChart } from '../compositions/useLiteratureAggChart'

export const BLiteratureAnalysisView = defineComponent({
  name: 'BLiteratureAnalysisView',
  i18n: {
    messages: {
      cn,
      en,
    },
  },
  props: {
    getTotalFn: {
      required: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      type: Function as PropType<() => Promise<ISearchResult<any> & { uncollapse_total?: number }>>,
    },
    queryService: {
      required: true,
      type: Object as PropType<IQueryService>,
    },
    loadingService: {
      required: true,
      type: Object as PropType<ILoadingService>,
    },
  },
  setup(props) {
    const showFullScreen = ref(false)
    const ins = getCurrentInstance()
    const activeFullScreen = ref<IUseLiteratureAggChartType | ''>('')

    const query = computed<IQuery>(() => {
      return {
        type: 'group',
        must: props.queryService.state.query.must,
        filter: props.queryService.state.query.filter,
      }
    })

    const chartTypes: IUseLiteratureAggChartType[] = ['year-line', 'drug-bar', 'target-bar', 'disease-bar', 'org-bar']

    const chartAggRecord = chartTypes.reduce<Record<IUseLiteratureAggChartType, ReturnType<typeof useLiteratureAggChart>>>(
      (pre, next) => ({
        ...pre,
        [next]: useLiteratureAggChart({ type: next, query }),
      }),
      {} as Record<IUseLiteratureAggChartType, ReturnType<typeof useLiteratureAggChart>>
    )

    const chartInitedRecord = reactive(
      chartTypes.reduce((pre, next) => ({ ...pre, [next]: false }), {} as Record<IUseLiteratureAggChartType, boolean>)
    )

    const lineChartEntityRecord = lineChartTypes.reduce<
      Record<IUseLiteratureAggChartType, ReturnType<typeof useAnalysisLiteratureOneDimChartEntity>>
    >(
      (pre, next) => ({
        ...pre,
        [next]: useAnalysisLiteratureOneDimChartEntity({
          chartType: 'line',
          data: chartAggRecord[next].aggData,
          categoryFieldName: chartAggRecord[next].categoryFieldName,
          countFieldName: chartAggRecord[next].countFieldName,
        }),
      }),
      {} as Record<IUseLiteratureAggChartType, ReturnType<typeof useAnalysisLiteratureOneDimChartEntity>>
    )

    const barChartEntityRecord = barChartTypes.reduce<Record<IUseLiteratureAggChartType, ReturnType<typeof useAnalysisLiteratureOneDimChartEntity>>>(
      (pre, next) => ({
        ...pre,
        [next]: useAnalysisLiteratureOneDimChartEntity({
          chartType: 'bar',
          data: chartAggRecord[next].aggData,
          categoryFieldName: chartAggRecord[next].categoryFieldName,
          countFieldName: chartAggRecord[next].countFieldName,
        }),
      }),
      {} as Record<IUseLiteratureAggChartType, ReturnType<typeof useAnalysisLiteratureOneDimChartEntity>>
    )

    const { handleClick: handleClickLineChart } = useAnalysisOneDimChartNavList({
      chartType: 'line',
      navList: 'paper',
      query,
      appendTo: 'filter',
    })

    const { handleClick: handleClickBarChart } = useAnalysisOneDimChartNavList({
      chartType: 'bar',
      navList: 'paper',
      query,
      appendTo: 'filter',
    })

    function getChartTitleByType(type: IUseLiteratureAggChartType) {
      switch (type) {
        case 'year-line':
          return ins?.proxy.$tc('analysisView.literature.trendAnalysis.publicationYear.title') || ''
        case 'drug-bar':
          return ins?.proxy.$tc('analysisView.literature.drugAnalysis.drug.title') || ''
        case 'target-bar':
          return ins?.proxy.$tc('analysisView.literature.targetAnalysis.target.title') || ''
        case 'disease-bar':
          return ins?.proxy.$tc('analysisView.literature.diseaseAnalysis.disease.title') || ''
        case 'org-bar':
          return ins?.proxy.$tc('analysisView.literature.orgAnalysis.org.title') || ''
        default:
          return ''
      }
    }

    function getChartDescByType(type: IUseLiteratureAggChartType) {
      switch (type) {
        case 'year-line':
          return ins?.proxy.$tc('analysisView.literature.trendAnalysis.publicationYear.desc') || ''
        case 'drug-bar':
          return ins?.proxy.$tc('analysisView.literature.drugAnalysis.drug.desc') || ''
        case 'target-bar':
          return ins?.proxy.$tc('analysisView.literature.targetAnalysis.target.desc') || ''
        case 'disease-bar':
          return ins?.proxy.$tc('analysisView.literature.diseaseAnalysis.disease.desc') || ''
        case 'org-bar':
          return ins?.proxy.$tc('analysisView.literature.orgAnalysis.org.desc') || ''
        default:
          return ''
      }
    }

    async function initData() {
      props.loadingService.action.setLoading(true)
      const rt = await props.getTotalFn()
      props.loadingService.action.setTotal(rt.total)
      props.loadingService.action.setUnCollapseTotal(rt.uncollapse_total)
      props.loadingService.action.setLoading(false)
    }

    function initAggData() {
      chartTypes.forEach((type) => {
        if (chartInitedRecord[type]) {
          handleFetchChartData(type)
        }
      })
    }

    function handleFetchChartData(type: IUseLiteratureAggChartType) {
      chartAggRecord[type].fetchAggData()
      chartInitedRecord[type] = true
    }

    function handleOpenFullScreen(type: IUseLiteratureAggChartType) {
      activeFullScreen.value = type
      showFullScreen.value = true
    }

    function handleCloseFullScreen() {
      showFullScreen.value = false
      activeFullScreen.value = ''
    }

    watch(
      () => [props.queryService.state.query.must, props.queryService.state.query.filter],
      (newValue, oldValue) => {
        if (isEqual(newValue, oldValue)) {
          return
        }
        initData()
      },
      {
        immediate: true,
      }
    )

    watch(query, () => {
      initAggData()
    })

    return {
      chartAggRecord,
      lineChartEntityRecord,
      getChartTitleByType,
      getChartDescByType,
      handleOpenFullScreen,
      handleCloseFullScreen,
      showFullScreen,
      activeFullScreen,
      handleFetchChartData,
      handleClickLineChart,
      handleClickBarChart,
      barChartEntityRecord,
    }
  },
  methods: {
    renderLineByType(type: IUseLiteratureAggChartType, cls = '') {
      const { model, controls, controlLayout, categoryFieldName, countFieldName, loading, isEmpty } = this.chartAggRecord[type]
      const { chartEntity, dataList } = this.lineChartEntityRecord[type]

      return (
        <vue-lazy-component key={`${type}_lazy`} style={{ minHeight: '400px' }} onInit={() => this.handleFetchChartData(type)}>
          <BAnalysisChartCard
            key={type}
            class={cls}
            title={this.getChartTitleByType(type)}
            desc={this.getChartDescByType(type)}
            isLoading={loading.value}
            isEmpty={isEmpty.value}
            height={600}
            controls={controls}
            layout={controlLayout}
            value={model}
            downloadCfg={{ visible: model.display !== 'table', type: 'chart-img' }}
            onOpen={() => this.handleOpenFullScreen(type)}
          >
            <BAnalysisLineEchart
              class={['h-full', { hidden: model.display === 'table' }]}
              onClickItem={this.handleClickLineChart}
              entity={chartEntity.value as AggLineChartEntity}
            />
            <BAnalysisLineTable
              class={[{ hidden: model.display === 'single-line' }]}
              categoryFieldName={categoryFieldName.value}
              countFieldName={countFieldName.value}
              list={dataList.value as AggOneDimDataItem[]}
              displayMode="table"
              height="100%"
            ></BAnalysisLineTable>
          </BAnalysisChartCard>
        </vue-lazy-component>
      )
    },
    renderLineFullScreen(type: IUseLiteratureAggChartType) {
      if (type !== this.activeFullScreen) return null

      const { model, controls, controlLayoutFullscreen, categoryFieldName, countFieldName, loading, isEmpty, settingDataPanelTitle } =
        this.chartAggRecord[type]

      const { chartEntity, dataList } = this.lineChartEntityRecord[type]

      return (
        <BAnalysisChartFullScreenCard
          key={`${type}_fullscreen`}
          class="h-full"
          title={this.getChartTitleByType(type)}
          desc={this.getChartDescByType(type)}
          isLoading={loading.value}
          isEmpty={isEmpty.value}
          controls={controls}
          displayType="single-line"
          layout={controlLayoutFullscreen}
          dataPanelTitle={settingDataPanelTitle.value}
          downloadCfg={{ visible: true, type: 'chart-img' }}
          value={model}
          onClose={this.handleCloseFullScreen}
        >
          <div>
            <div class="p-4 border border-solid border-gray-40 rounded">
              <BAnalysisLineEchart
                onClickItem={this.handleClickLineChart}
                entity={chartEntity.value as AggLineChartEntity}
                style={{ height: '600px' }}
              />
            </div>
            <div class="mt-8">
              <BAnalysisLineTable
                categoryFieldName={categoryFieldName.value}
                countFieldName={countFieldName.value}
                list={dataList.value as AggOneDimDataItem[]}
                displayMode="record"
              ></BAnalysisLineTable>
            </div>
          </div>
        </BAnalysisChartFullScreenCard>
      )
    },
    renderBarByType(type: IUseLiteratureAggChartType, cls = '') {
      const { model, controls, controlLayout, categoryFieldName, countFieldName, loading, isEmpty, settingChartPanelTitle } =
        this.chartAggRecord[type]
      const { chartEntity, dataList } = this.barChartEntityRecord[type]

      const height = Math.max(600, dataList.value.length * 80)

      return (
        <vue-lazy-component key={`${type}_lazy`} style={{ minHeight: '400px' }} onInit={() => this.handleFetchChartData(type)}>
          <BAnalysisChartCard
            key={type}
            class={cls}
            title={this.getChartTitleByType(type)}
            desc={this.getChartDescByType(type)}
            isLoading={loading.value}
            isEmpty={isEmpty.value}
            height={height}
            controls={controls}
            layout={controlLayout}
            value={model}
            settingTitle={settingChartPanelTitle.value}
            downloadCfg={{ visible: model.display !== 'table', type: 'chart-img' }}
            onOpen={() => this.handleOpenFullScreen(type)}
          >
            <BAnalysisBarEchart
              class={['h-full', { hidden: model.display === 'table' }]}
              displayType={model.display}
              onClickItem={this.handleClickBarChart}
              entity={chartEntity.value as AggBarChartEntity}
            ></BAnalysisBarEchart>
            <BAnalysisBarTable
              class={[{ hidden: model.display === 'horizontal-bar' }]}
              categoryFieldName={categoryFieldName.value}
              countFieldName={countFieldName.value}
              list={dataList.value as AggOneDimDataItem[]}
              displayMode="table"
              height="100%"
            ></BAnalysisBarTable>
          </BAnalysisChartCard>
        </vue-lazy-component>
      )
    },
    renderBarFullScreen(type: IUseLiteratureAggChartType) {
      if (type !== this.activeFullScreen) return null

      const {
        model,
        controls,
        controlLayoutFullscreen,
        categoryFieldName,
        countFieldName,
        loading,
        isEmpty,
        settingChartPanelTitle,
        settingDataPanelTitle,
      } = this.chartAggRecord[type]

      const { chartEntity, dataList } = this.barChartEntityRecord[type]

      const height = Math.max(600, dataList.value.length * 80)

      return (
        <BAnalysisChartFullScreenCard
          key={`${type}_fullscreen`}
          class="h-full"
          title={this.getChartTitleByType(type)}
          desc={this.getChartDescByType(type)}
          isLoading={loading.value}
          isEmpty={isEmpty.value}
          controls={controls}
          displayType="horizontal-bar"
          layout={controlLayoutFullscreen}
          chartPanelTitle={settingChartPanelTitle.value}
          dataPanelTitle={settingDataPanelTitle.value}
          downloadCfg={{ visible: true, type: 'chart-img' }}
          value={model}
          onClose={this.handleCloseFullScreen}
        >
          <div>
            <div class="p-4 border border-solid border-gray-40 rounded">
              <BAnalysisBarEchart
                displayType={model.display}
                onClickItem={this.handleClickBarChart}
                entity={chartEntity.value as AggBarChartEntity}
                style={{ height: `${height}px` }}
              ></BAnalysisBarEchart>
            </div>
            <div class="mt-8">
              <BAnalysisBarTable
                categoryFieldName={categoryFieldName.value}
                countFieldName={countFieldName.value}
                list={dataList.value as AggOneDimDataItem[]}
                displayMode="record"
              ></BAnalysisBarTable>
            </div>
          </div>
        </BAnalysisChartFullScreenCard>
      )
    },
  },
  render() {
    return (
      <div class="p-4 pb-16 h-full overflow-y-auto">
        <div class={[$classes.blockTitle, 'mb-4']}>{this.$t('analysisView.literature.trendAnalysisTitle')}</div>
        {this.renderLineByType('year-line')}
        <div class={[$classes.blockTitle, 'mt-8 mb-4']}>{this.$t('analysisView.literature.drugAnalysisTitle')}</div>
        {this.renderBarByType('drug-bar')}
        <div class={[$classes.blockTitle, 'mt-8 mb-4']}>{this.$t('analysisView.literature.targetAnalysisTitle')}</div>
        {this.renderBarByType('target-bar')}
        <div class={[$classes.blockTitle, 'mt-8 mb-4']}>{this.$t('analysisView.literature.diseaseAnalysisTitle')}</div>
        {this.renderBarByType('disease-bar')}
        <div class={[$classes.blockTitle, 'mt-8 mb-4']}>{this.$t('analysisView.literature.orgAnalysisTitle')}</div>
        {this.renderBarByType('org-bar')}
        <div class={[$classes.fullscreen, { hidden: !this.showFullScreen }]}>
          {this.renderLineFullScreen('year-line')}
          {this.renderBarFullScreen('drug-bar')}
          {this.renderBarFullScreen('target-bar')}
          {this.renderBarFullScreen('disease-bar')}
          {this.renderBarFullScreen('org-bar')}
        </div>
      </div>
    )
  },
})
